const reducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

const easing = {
  ios: 'cubic-bezier(0.16, 1, 0.3, 1)',
  iosIn: 'cubic-bezier(0.32, 0, 0.67, 0)',
  iosInOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
};

export interface AnimateOpts {
  duration?: number;
  delay?: number;
  easing?: keyof typeof easing;
  fill?: FillMode;
}

const apply = (el: Element, keyframes: Keyframe[], opts: AnimateOpts = {}): Animation | null => {
  if (reducedMotion()) return null;
  return el.animate(keyframes, {
    duration: opts.duration ?? 250,
    delay: opts.delay ?? 0,
    easing: easing[opts.easing ?? 'ios'],
    fill: opts.fill ?? 'forwards',
  });
};

export const enter = (el: Element, opts: AnimateOpts = {}): Animation | null =>
  apply(
    el,
    [
      { opacity: 0, transform: 'translateY(8px)' },
      { opacity: 1, transform: 'translateY(0)' },
    ],
    { duration: 320, ...opts },
  );

export const exit = (el: Element, opts: AnimateOpts = {}): Animation | null =>
  apply(
    el,
    [
      { opacity: 1, transform: 'translateY(0)' },
      { opacity: 0, transform: 'translateY(-4px)' },
    ],
    { duration: 200, easing: 'iosIn', ...opts },
  );

export const glowPulse = (el: HTMLElement, color = 'var(--color-accent)'): Animation | null =>
  apply(
    el,
    [
      { boxShadow: `0 0 0 0 ${color}33, 0 0 0 0 ${color}00` },
      { boxShadow: `0 0 0 8px ${color}26, 0 0 16px 8px ${color}1a` },
      { boxShadow: `0 0 0 0 ${color}00, 0 0 0 0 ${color}00` },
    ],
    { duration: 700, easing: 'iosInOut' },
  );

export const staggerEnter = (
  parent: Element | null,
  options: { selector?: string; delayStep?: number; baseDelay?: number; duration?: number } = {},
): void => {
  if (!parent) return;
  const items = parent.querySelectorAll<HTMLElement>(options.selector ?? ':scope > *');
  items.forEach((item, i) => {
    enter(item, {
      delay: (options.baseDelay ?? 0) + i * (options.delayStep ?? 40),
      duration: options.duration ?? 320,
    });
  });
};

export const shimmer = (el: HTMLElement): Animation | null =>
  apply(
    el,
    [{ backgroundPosition: '-200% 0' }, { backgroundPosition: '200% 0' }],
    { duration: 1500, fill: 'forwards', easing: 'iosInOut' },
  );

export const successFlash = (el: HTMLElement): Animation | null =>
  apply(
    el,
    [
      { transform: 'scale(1)' },
      { transform: 'scale(1.02)' },
      { transform: 'scale(1)' },
    ],
    { duration: 320, easing: 'ios' },
  );

export const onMountAnimate = (
  animator: (el: HTMLElement, opts?: AnimateOpts) => Animation | null,
  opts?: AnimateOpts,
): ((el: HTMLElement) => void) => {
  return (el) => {
    if (!el) return;
    queueMicrotask(() => animator(el, opts));
  };
};
